import { Implements, Service, ServiceLifetime } from '@gitlab/needle';
import type { McpConfigPathCandidate } from '@gitlab-org/ai-configuration';
import { McpConfigPathProvider } from '@gitlab-org/ai-configuration';
import { PluginComponentsReader } from '@gitlab-org/duo-plugin-marketplace/node';

@Implements(McpConfigPathProvider)
@Service({
  dependencies: [PluginComponentsReader],
  lifetime: ServiceLifetime.Singleton,
})
export class PluginMcpConfigProvider implements McpConfigPathProvider {
  #reader: PluginComponentsReader;

  constructor(reader: PluginComponentsReader) {
    this.#reader = reader;
  }

  /**
   * `namePrefix` namespaces server names, so it has to be as unique as the approval key:
   * a bare plugin name is not, since two marketplaces can each ship a `<name>`, and the
   * two would then collide on one server name with only the first surviving the merge.
   * The marketplace is dropped from `displayNamePrefix` because it makes for a long name
   * and users rarely have two plugins sharing one; where it matters — the approval
   * prompt, and the detail view — the config path is shown alongside.
   */
  async getCandidates(workspacePath?: string): Promise<McpConfigPathCandidate[]> {
    const configs = await this.#reader.listEnabledMcpConfigs(workspacePath ? [workspacePath] : []);
    return configs.map((config) => ({
      path: config.configPath,
      id: config.pluginId,
      variables: { DUO_PLUGIN_ROOT: config.pluginRoot },
      namePrefix: `${config.pluginId}:`,
      displayNamePrefix: `${config.pluginName}:`,
    }));
  }
}
